import { gql, useQuery } from "urql";
import type { TypedDocumentNode } from "urql";
import { useOutletContext } from "react-router";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Kpi } from "@acto/taskpane/core/domain";
import {
  Kpi as GqlKpi,
  type Granularity as GqlGranularity,
  type KpiComparisonMode as GqlKpiComparisonMode,
} from "@acto/gql/graphql";
import { toGqlGranularity, toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import type { KpiPairTrend } from "@acto/taskpane/field-sales-2/components/primitives/KpiPairCard";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { formatCompact, formatCompactCurrency } from "@acto/taskpane/field-sales-2/utils/format";
import { buildKpiTrend, type KpiSummaryValueData } from "./buildKpiTrend";
import { selectGranularity } from "./selectGranularity";
import { toKpiPairTrend } from "./toKpiPairTrend";

interface OrdersData {
  incomingOrder: number | null;
  count: number | null;
}

interface OrdersTimeSeries {
  dataPoints: {
    periodStart: string;
    previousPeriodStart: string | null;
    current: OrdersData;
    previous: OrdersData | null;
  }[];
  totals: OrdersData;
  previousTotals: OrdersData;
  analysisPeriod: { previous: { startDate: string; endDate: string } };
}

interface CustomerOrdersTrendQueryVariables {
  clientId: string;
  dateRange: { startDate: string; endDate: string };
  granularity: GqlGranularity;
  comparisonMode: GqlKpiComparisonMode;
  productGroupId?: string;
}

const CustomerOrdersTrendQuery: TypedDocumentNode<
  { kpisByTimeSeries: OrdersTimeSeries | null },
  CustomerOrdersTrendQueryVariables
> = gql`
  query GetCustomerOrdersTrend(
    $clientId: ID!
    $dateRange: DateRangeInput!
    $granularity: Granularity!
    $comparisonMode: KpiComparisonMode!
    $productGroupId: ID
  ) {
    kpisByTimeSeries(
      primaryClientId: $clientId
      dateRange: $dateRange
      granularity: $granularity
      comparisonMode: $comparisonMode
      productGroupId: $productGroupId
    ) {
      dataPoints {
        periodStart
        previousPeriodStart
        current {
          incomingOrder
          count
        }
        previous {
          incomingOrder
          count
        }
      }
      totals {
        incomingOrder
        count
      }
      previousTotals {
        incomingOrder
        count
      }
      analysisPeriod {
        previous {
          startDate
          endDate
        }
      }
    }
  }
`;

function toSummaryValue(series: OrdersTimeSeries, field: keyof OrdersData, kpi: GqlKpi): KpiSummaryValueData {
  const totalValue = series.totals[field] ?? 0;
  const previousTotal = series.previousTotals[field];

  return {
    kpi,
    totalValue,
    timeSeries: {
      dataPoints: series.dataPoints.map((point) => ({
        periodStart: point.periodStart,
        value: point.current[field] ?? 0,
        previousPeriodStart: point.previousPeriodStart,
      })),
    },
    // A previous window without data means no comparison, not a drop to zero.
    comparison:
      previousTotal == null
        ? null
        : {
            totalValue: previousTotal,
            change: {
              percentageChange: previousTotal !== 0 ? ((totalValue - previousTotal) / previousTotal) * 100 : null,
            },
            range: series.analysisPeriod.previous,
          },
    comparisonTimeSeries:
      previousTotal == null
        ? null
        : {
            dataPoints: series.dataPoints.map((point) => ({
              periodStart: point.periodStart,
              value: point.previous?.[field] ?? 0,
            })),
          },
  };
}

export interface CustomerOrdersTrend {
  trend: KpiPairTrend;
  orderCount: number | undefined;
}

/**
 * Order intake and order count for the date range in the customer outlet context, compared using the tenant's
 * comparison mode. `productGroupId` may also name a single product.
 */
export function useCustomerOrdersTrend(clientId: string | undefined, productGroupId?: string): CustomerOrdersTrend {
  const { dateRange, isCustomRange } = useOutletContext<CustomerOutletContext>();
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const comparisonMode = useComparisonModeContext();
  const tKpi = useTranslations("kpi");
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");
  const tOrders = useTranslations("fieldSalesTwo.analysis.orders");

  const granularity = selectGranularity(dateRange);

  const [result] = useQuery({
    query: CustomerOrdersTrendQuery,
    variables: {
      clientId: clientId ?? "",
      dateRange: dateRange ?? { startDate: "", endDate: "" },
      granularity: toGqlGranularity(granularity),
      comparisonMode: toGqlKpiComparisonMode(comparisonMode),
      productGroupId,
    },
    requestPolicy: "cache-first",
    pause: !clientId || !dateRange,
  });

  const series = result.data?.kpisByTimeSeries ?? null;

  const build = (selectedKpi: Kpi, field: keyof OrdersData, kpi: GqlKpi) =>
    buildKpiTrend({
      values: series ? [toSummaryValue(series, field, kpi)] : [],
      fetching: result.fetching,
      stale: result.stale,
      selectedKpi,
      comparisonMode,
      dateRange,
      isCustomRange,
      granularity,
      locale,
      currency,
      tKpi,
    });

  const trend = toKpiPairTrend(
    // Order intake is tagged as REVENUE so it is matched and formatted as currency; there is no order-intake KPI.
    build(Kpi.REVENUE, "incomingOrder", GqlKpi.Revenue),
    build(Kpi.ORDERS, "count", GqlKpi.Orders),
    {
      barKpi: tOrders("orderIntake"),
      lineKpi: tOrders("orderCount"),
      currentPeriodLabel: tAnalysis("currentPeriod"),
      formatBarAxis: (value) => formatCompactCurrency(value, currency, locale),
      formatLineAxis: (value) => tOrders("pieces", { value: formatCompact(value) }),
    },
  );

  return { trend, orderCount: series?.totals.count ?? undefined };
}
